/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Popover, OverlayTrigger } from 'react-bootstrap'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { NoInfo } from 'plugin-codaf-risk-ext-bowtie/BowtieAlarmsNotificationSettings/styles'
import { useDimensions } from 'plugin-codaf-risk-ext-bowtie/hooks/reportsDimensions'
import { getAlarmCount, getAlarmGraph } from 'plugin-codaf-risk-ext-bowtie/services'
import { IBowtie } from 'plugin-codaf-risk-ext-bowtie/types'
import { IAlarmEvent } from 'plugin-codaf-risk-ext-bowtie/types/alarms'

import { IOverviewAlarmEvent, IOverviewAsset } from '../AssetSelector/AssetSelector'
import { AlarmCountCard, DashboardAlarmList, DashboardAlarmDetails } from './components'
import { AlarmGraph, AlarmGraphField, AlarmsDashboardContainer, GraphicPopover } from './styles'

interface IProps {
    setShowAlarmsDashboard: (k: boolean) => void
    asset: IOverviewAsset
    alarms: Map<number, IAlarmEvent>
    setAlarmData: (k: IOverviewAlarmEvent) => void
    alarmData: IOverviewAlarmEvent
    setSelectedBowtie: (k: number) => void
    alarmBowties: IBowtie[]
}

interface ICardData {
    totalFatal: number
    totalWarn: number
    totalError: number
    totalInfo: number
}

interface IGraphic {
    day: number
    events: IAlarmEvent[]
}

interface IGraphData {
    day: number
    total: number
    fatal: number
    warn: number
    error: number
    info: number
    size?: number
}

export const AlarmsDashboard: React.FC<IProps> = ({
    setShowAlarmsDashboard,
    asset,
    alarms,
    setAlarmData,
    alarmData,
    setSelectedBowtie,
    alarmBowties
}) => {
    const [cardData, setCardData] = useState<ICardData>({ totalError: 0, totalFatal: 0, totalInfo: 0, totalWarn: 0 })
    const [graphicData, setGraphicData] = useState<IGraphData[]>()
    const [loadingGraph, setLoadingGraph] = useState<boolean>()
    const [loadingCount, setLoadingCount] = useState<boolean>()
    const [timeline, setTimeline] = useState<number[]>()

    const handleGetCountData = async (): Promise<void> => {
        try {
            setLoadingCount(true)
            const counter: ICardData[] = []

            for (let i = 0; i < asset.overviewAlarms.length; i++) {
                counter.push(await getAlarmCount({ entityId: asset.overviewAlarms[i].id }))
            }

            const error = counter.reduce((a, b) => a + b.totalError, 0)
            const warn = counter.reduce((a, b) => a + b.totalWarn, 0)
            const fatal = counter.reduce((a, b) => a + b.totalFatal, 0)
            const info = counter.reduce((a, b) => a + b.totalInfo, 0)

            setCardData({ totalError: error, totalFatal: fatal, totalInfo: info, totalWarn: warn })
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on alarm count.'))
        } finally {
            setLoadingCount(false)
        }
    }

    const preparingDataToGraph = (events: IAlarmEvent[]): IGraphData[] => {
        const days: IGraphic[] = []

        for (const event of events) {
            let dayIndex = days.findIndex(day => day.day === new Date(event.date).getDate())

            if (dayIndex === -1) {
                days.push({ day: new Date(event.date).getDate(), events: [] })
                dayIndex = days.length - 1
            }

            days[dayIndex].events.push(event)
        }

        const dataToReturn: IGraphData[] = []

        days.map(el => {
            const fatal = el.events.filter(filter => filter.status === 'FATAL').length
            const info = el.events.filter(filter => filter.status === 'INFO').length
            const warn = el.events.filter(filter => filter.status === 'WARN').length
            const error = el.events.filter(filter => filter.status === 'ERROR').length
            const total = el.events.length

            dataToReturn.push({ day: el.day, error: error, info: info, warn: warn, fatal: fatal, total: total })
        })

        return dataToReturn
    }

    const handleGraphData = async (): Promise<void> => {
        try {
            setLoadingGraph(true)
            const graphic: IAlarmEvent[] = []

            for (let i = 0; i < asset.overviewAlarms.length; i++) {
                const graphToPush = await getAlarmGraph({ entityId: asset.overviewAlarms[i].id })
                graphToPush.data.map(ev => graphic.push(ev))
            }
            // const graphic = await getAlarmGraph({entityId: 956469362})
            const graphicDataConverted = preparingDataToGraph(graphic)
            setGraphicData(graphicDataConverted)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on render alarm graphic.'))
        } finally {
            setLoadingGraph(false)
        }
    }

    const renderGraph = (): (JSX.Element | JSX.Element[])[] => {
        const catchingBiggestValue = graphicData?.sort((a, b) => {
            if (a.total > b.total) return -1
            if (a.total < b.total) return 1

            return 0
        })

        const onePercentGraph = catchingBiggestValue ? catchingBiggestValue[0]?.total / 100 : 1
        const fieldWithPercent = []

        for (let i = 0; i < graphicData?.length; i++) {
            fieldWithPercent.push(catchingBiggestValue[i]?.total / onePercentGraph)
        }

        const objectToRender: IGraphData[] = []

        for (let i = 0; i < catchingBiggestValue?.length; i++) {
            objectToRender.push({
                day: catchingBiggestValue[i].day,
                error: catchingBiggestValue[i].error,
                fatal: catchingBiggestValue[i].fatal,
                info: catchingBiggestValue[i].info,
                total: catchingBiggestValue[i].total,
                warn: catchingBiggestValue[i].warn,
                size: fieldWithPercent[i]
            })
        }

        const graphData = [
            { color: 'scarlet', status: i18n('Fatal Alerts') },
            { color: 'red', status: i18n('Error Alerts') },
            { color: 'orange', status: i18n('Warn Alerts') },
            { color: 'blue', status: i18n('Info Alerts') }
        ]

        return timeline?.map((tl, index) => {
            if (objectToRender?.filter(el => el.day === tl).length > 0) {
                return objectToRender
                    ?.filter(el => el.day === tl)
                    .map(el => {
                        const popoverHoverFocus = (
                            <Popover positionTop={50} id="popover-trigger-hover-focus">
                                <GraphicPopover>
                                    {graphData.map(graphStatus => (
                                        <div className="field" key={graphStatus.status}>
                                            <div className={`color ${graphStatus.color}`}></div>
                                            {el.fatal} {graphStatus.status}
                                        </div>
                                    ))}
                                </GraphicPopover>
                            </Popover>
                        )

                        return (
                            <OverlayTrigger
                                key={index}
                                trigger={['hover', 'focus']}
                                placement="top"
                                overlay={popoverHoverFocus}
                            >
                                <AlarmGraphField
                                    key={el.day}
                                    total={el.total}
                                    error={el.error}
                                    fatal={el.fatal}
                                    info={el.info}
                                    warn={el.warn}
                                    sizeField={el.size}
                                    first={index === 0}
                                    dayOne={tl === 1}
                                >
                                    <div className="graphFatal"></div>
                                    <div className="graphWarn"></div>
                                    <div className="graphError"></div>
                                    <div className="graphInfo"></div>
                                </AlarmGraphField>
                            </OverlayTrigger>
                        )
                    })
            } else {
                return (
                    <AlarmGraphField
                        key={tl}
                        total={0}
                        error={0}
                        fatal={0}
                        info={0}
                        warn={0}
                        sizeField={0}
                        first={index === 0}
                        dayOne={tl === 1}
                    >
                        <div className="graphFatal"></div>
                        <div className="graphWarn"></div>
                        <div className="graphError"></div>
                        <div className="graphInfo"></div>
                    </AlarmGraphField>
                )
            }
        })
    }

    const renderTimeLine = (): void => {
        const days = []
        for (let i = 28; i >= -1; i--) {
            const day = new Date().getTime() - 86400000 * i
            const convertedDay = new Date(day).getDate()
            days.push(convertedDay)
        }

        setTimeline(days)
    }

    useEffect(() => {
        handleGetCountData()
        handleGraphData()
        renderTimeLine()
    }, [])

    return (
        <AlarmsDashboardContainer>
            <i className="fas fa-times close-button" onClick={() => setShowAlarmsDashboard(false)}></i>
            <div className={'row-dashboard'}>
                <div className="count-cards">
                    <AlarmCountCard
                        alarmCount={cardData?.totalFatal || 0}
                        alarmStatus={i18n('Fatal Alerts')}
                        color={'#AE1F1F'}
                        loading={loadingCount}
                    />
                    <AlarmCountCard
                        alarmCount={cardData?.totalError || 0}
                        alarmStatus={i18n('Error Alerts')}
                        color={'#FF4000'}
                        loading={loadingCount}
                    />
                    <AlarmCountCard
                        alarmCount={cardData?.totalWarn || 0}
                        alarmStatus={i18n('Warn Alerts')}
                        color={'#FF9326'}
                        loading={loadingCount}
                    />
                    <AlarmCountCard
                        alarmCount={cardData?.totalInfo || 0}
                        alarmStatus={i18n('Info Alerts')}
                        color={'#00BFFF'}
                        loading={loadingCount}
                    />
                </div>
                <AlarmGraph>
                    {loadingGraph ? (
                        <NoInfo>
                            <i className="fa fa-spinner fa-spin" />
                        </NoInfo>
                    ) : (
                        <>
                            <div className="graphicHeader">
                                <div className="graphTitle">
                                    <div className="title-alert">{i18n('ALERTS BY DAY')}</div>
                                    <div className="title-days">
                                        <i className="fas fa-lock"></i>
                                        <span>{i18n('Last 30 days')}</span>
                                    </div>
                                </div>
                                <div className="graphSubtitle">
                                    <div className="color blue"></div>
                                    <span>{i18n('info')}</span>
                                    <div className="color orange"></div>
                                    <span>{i18n('warn')}</span>
                                    <div className="color red"></div>
                                    <span>{i18n('error').toLowerCase()}</span>
                                    <div className="color scarlet"></div>
                                    <span>{i18n('fatal')}</span>
                                </div>
                            </div>
                            <div className="graphContainer">
                                <div className="graphFields">{renderGraph()}</div>
                                <div className="graphTimeline">
                                    {timeline?.map((el, index) => {
                                        return (
                                            <div key={index} className={el === 1 ? 'day day1' : 'day'}>
                                                <div className="mark"></div>
                                                <span>{el === 1 ? `${el}/${new Date().getMonth() + 1}` : el}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </AlarmGraph>
            </div>
            <div className={'row-dashboard bottom'}>
                <DashboardAlarmList
                    asset={asset}
                    alarms={alarms}
                    setAlarmData={setAlarmData}
                    setSelectedBowtie={setSelectedBowtie}
                    alarmBowties={alarmBowties}
                />
                <DashboardAlarmDetails
                    setAlarmData={setAlarmData}
                    alarmData={alarmData}
                    alarms={alarms}
                    alarmBowties={alarmBowties}
                />
            </div>
        </AlarmsDashboardContainer>
    )
}
